import * as XLSX from "xlsx";
import convertUnit from "utils/convertUnit";
import { getComponentTemplate } from "utils/enclosure";
import defaultTemplate from "features/CreateModel/Templates/default-v11-13.json";
import { getOptions } from "utils/fields";
import { removeUndefined } from "utils/generalUtils/generalUtils";
import takeoffCodes from "./takeoffCodes.json";

const parseEvaluationDate = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const dateStr = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
    }

    const serial = typeof value === "number" ? value : parseFloat(dateStr);
    if (Number.isFinite(serial) && serial > 0) {
        const parsed = XLSX.SSF.parse_date_code(serial);
        if (parsed?.y && parsed?.m && parsed?.d) {
            return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(2, "0")}`;
        }
    }

    return null;
};

// Group name to component type mapping
const GROUP_TO_COMPONENT_TYPE = {
    Basement1: "basement",
    Basement2: "basement",
    Basement3: "basement",
    Basement4: "basement",
    Basement5: "basement",
    Basement6: "basement",
    Basement7: "basement",
    Basement8: "basement",
    Basement9: "basement",
    Basement10: "basement",
    Basement11: "basement",
    Basement12: "basement",
    CathedralBufferedWall1: "wall",
    CathedralBufferedWall2: "wall",
    CathedralBufferedWall3: "wall",
    CathedralBufferedWall4: "wall",
    CathedralBufferedWall5: "wall",
    CathedralBufferedWall6: "wall",
    CathedralBufferedWall7: "wall",
    CathedralBufferedWall8: "wall",
    CathedralBufferedWall9: "wall",
    CathedralBufferedWall10: "wall",
    CathedralBufferedWall11: "wall",
    CathedralBufferedWall12: "wall",
    CathedralCeiling1: "ceiling",
    CathedralCeiling2: "ceiling",
    CathedralCeiling3: "ceiling",
    CathedralCeiling4: "ceiling",
    CathedralCeiling5: "ceiling",
    CathedralCeiling6: "ceiling",
    CathedralCeiling7: "ceiling",
    CathedralCeiling8: "ceiling",
    CathedralCeiling9: "ceiling",
    CathedralCeiling10: "ceiling",
    CathedralCeiling11: "ceiling",
    CathedralCeiling12: "ceiling",
    Crawlspace1: "crawlspace",
    Crawlspace2: "crawlspace",
    Crawlspace3: "crawlspace",
    Crawlspace4: "crawlspace",
    Crawlspace5: "crawlspace",
    Crawlspace6: "crawlspace",
    Crawlspace7: "crawlspace",
    Crawlspace8: "crawlspace",
    Crawlspace9: "crawlspace",
    Crawlspace10: "crawlspace",
    Crawlspace11: "crawlspace",
    Crawlspace12: "crawlspace",
    FlatCeiling1: "ceiling",
    FlatCeiling2: "ceiling",
    FlatCeiling3: "ceiling",
    FlatCeiling4: "ceiling",
    FlatCeiling5: "ceiling",
    FlatCeiling6: "ceiling",
    FlatCeiling7: "ceiling",
    FlatCeiling8: "ceiling",
    FlatCeiling9: "ceiling",
    FlatCeiling10: "ceiling",
    FlatCeiling11: "ceiling",
    FlatCeiling12: "ceiling",
    Floor1: "expFloor",
    Floor2: "expFloor",
    Floor3: "expFloor",
    Floor4: "expFloor",
    Floor5: "expFloor",
    Floor6: "expFloor",
    Floor7: "expFloor",
    Floor8: "expFloor",
    Floor9: "expFloor",
    Floor10: "expFloor",
    Floor11: "expFloor",
    Floor12: "expFloor",
    Lvl0Headers1: "floorHeader",
    Lvl0Headers2: "floorHeader",
    Lvl0Headers3: "floorHeader",
    Lvl0Headers4: "floorHeader",
    Lvl0Headers5: "floorHeader",
    Lvl0Headers6: "floorHeader",
    Lvl0Headers7: "floorHeader",
    Lvl0Headers8: "floorHeader",
    Lvl0Headers9: "floorHeader",
    Lvl0Headers10: "floorHeader",
    Lvl0Headers11: "floorHeader",
    Lvl0Headers12: "floorHeader",
    Lvl1Headers1: "floorHeader",
    Lvl1Headers2: "floorHeader",
    Lvl1Headers3: "floorHeader",
    Lvl1Headers4: "floorHeader",
    Lvl1Headers5: "floorHeader",
    Lvl1Headers6: "floorHeader",
    Lvl1Headers7: "floorHeader",
    Lvl1Headers8: "floorHeader",
    Lvl1Headers9: "floorHeader",
    Lvl1Headers10: "floorHeader",
    Lvl1Headers11: "floorHeader",
    Lvl1Headers12: "floorHeader",
    Lvl2Headers1: "floorHeader",
    Lvl2Headers2: "floorHeader",
    Lvl2Headers3: "floorHeader",
    Lvl2Headers4: "floorHeader",
    Lvl2Headers5: "floorHeader",
    Lvl2Headers6: "floorHeader",
    Lvl2Headers7: "floorHeader",
    Lvl2Headers8: "floorHeader",
    Lvl2Headers9: "floorHeader",
    Lvl2Headers10: "floorHeader",
    Lvl2Headers11: "floorHeader",
    Lvl2Headers12: "floorHeader",
    Lvl0Wall1: "wall",
    Lvl0Wall2: "wall",
    Lvl0Wall3: "wall",
    Lvl0Wall4: "wall",
    Lvl0Wall5: "wall",
    Lvl0Wall6: "wall",
    Lvl0Wall7: "wall",
    Lvl0Wall8: "wall",
    Lvl0Wall9: "wall",
    Lvl0Wall10: "wall",
    Lvl0Wall11: "wall",
    Lvl0Wall12: "wall",
    Lvl1Wall1: "wall",
    Lvl1Wall2: "wall",
    Lvl1Wall3: "wall",
    Lvl1Wall4: "wall",
    Lvl2Wall1: "wall",
    Lvl2Wall2: "wall",
    Lvl2Wall3: "wall",
    Lvl2Wall4: "wall",
    Lvl2Wall5: "wall",
    Lvl2Wall6: "wall",
    Lvl2Wall7: "wall",
    Lvl2Wall8: "wall",
    Lvl2Wall9: "wall",
    Lvl2Wall10: "wall",
    Lvl2Wall11: "wall",
    Lvl2Wall12: "wall",
    Lvl3Wall1: "wall",
    Lvl3Wall2: "wall",
    Lvl3Wall3: "wall",
    Lvl3Wall4: "wall",
    Lvl3Wall5: "wall",
    Lvl3Wall6: "wall",
    Lvl3Wall7: "wall",
    Lvl3Wall8: "wall",
    Lvl3Wall9: "wall",
    Lvl3Wall10: "wall",
    Lvl3Wall11: "wall",
    Lvl3Wall12: "wall",
    Lvl4Wall1: "wall",
    Lvl4Wall2: "wall",
    Lvl4Wall3: "wall",
    Lvl4Wall4: "wall",
    Lvl4Wall5: "wall",
    Lvl4Wall6: "wall",
    Lvl4Wall7: "wall",
    Lvl4Wall8: "wall",
    Lvl4Wall9: "wall",
    Lvl4Wall10: "wall",
    Lvl4Wall11: "wall",
    Lvl4Wall12: "wall",
    RaisedCeiling1: "ceiling",
    RaisedCeiling2: "ceiling",
    RaisedCeiling3: "ceiling",
    RaisedCeiling4: "ceiling",
    RaisedCeiling5: "ceiling",
    RaisedCeiling6: "ceiling",
    RaisedCeiling7: "ceiling",
    RaisedCeiling8: "ceiling",
    RaisedCeiling9: "ceiling",
    RaisedCeiling10: "ceiling",
    RaisedCeiling11: "ceiling",
    RaisedCeiling12: "ceiling",
    RaisedCeilingBufferedWall1: "wall",
    RaisedCeilingBufferedWall2: "wall",
    RaisedCeilingBufferedWall3: "wall",
    RaisedCeilingBufferedWall4: "wall",
    RaisedCeilingBufferedWall5: "wall",
    RaisedCeilingBufferedWall6: "wall",
    RaisedCeilingBufferedWall7: "wall",
    RaisedCeilingBufferedWall8: "wall",
    RaisedCeilingBufferedWall9: "wall",
    RaisedCeilingBufferedWall10: "wall",
    RaisedCeilingBufferedWall11: "wall",
    RaisedCeilingBufferedWall12: "wall",
    RaisedCeilingWall1: "wall",
    RaisedCeilingWall2: "wall",
    RaisedCeilingWall3: "wall",
    RaisedCeilingWall4: "wall",
    RaisedCeilingWall5: "wall",
    RaisedCeilingWall6: "wall",
    RaisedCeilingWall7: "wall",
    RaisedCeilingWall8: "wall",
    RaisedCeilingWall9: "wall",
    RaisedCeilingWall10: "wall",
    RaisedCeilingWall11: "wall",
    RaisedCeilingWall12: "wall",
    SlopedCeiling1: "ceiling",
    SlopedCeiling2: "ceiling",
    SlopedCeiling3: "ceiling",
    SlopedCeiling4: "ceiling",
    SlopedCeiling5: "ceiling",
    SlopedCeiling6: "ceiling",
    SlopedCeiling7: "ceiling",
    SlopedCeiling8: "ceiling",
    SlopedCeiling9: "ceiling",
    SlopedCeiling10: "ceiling",
    SlopedCeiling11: "ceiling",
    SlopedCeiling12: "ceiling",
    SOG1: "slab",
    SOG2: "slab",
    SOG3: "slab",
    SOG4: "slab",
    SOG5: "slab",
    SOG6: "slab",
    SOG7: "slab",
    SOG8: "slab",
    SOG9: "slab",
    SOG10: "slab",
    SOG11: "slab",
    SOG12: "slab",
};

// Level to category mapping
const LEVEL_TO_CATEGORY = {
    0: "foundation",
    1: "first",
    2: "second",
    3: "third",
    4: "third",
};

const compassDirectionMapToId = {
    S: 0,
    SE: 1,
    E: 2,
    NE: 3,
    N: 4,
    NW: 5,
    W: 6,
    SW: 7,
};

// Convert Imperial to Metric
const convertToMetric = (value, type = "length", inputUnit = "in") => {
    if (value === null || value === undefined || value === "") {
        return value;
    }
    const numValue = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(numValue)) {
        return value;
    }
    let outputUnit;
    if (type === "length") {
        outputUnit = "m";
    } else if (type === "area") {
        outputUnit = "m2";
    } else if (type === "volume") {
        outputUnit = "m3";
    } else {
        outputUnit = "m"; // Default to meters
    }
    return parseFloat(
        convertUnit({
            value: numValue,
            type,
            inputUnit,
            outputUnit,
        }).toFixed(4)
    );
};

// Get category from level
const getCategoryFromLevel = (level) => {
    const levelNum = typeof level === "number" ? level : parseInt(level);
    return LEVEL_TO_CATEGORY[levelNum] || "other";
};

// Generate sequential component ID
const generateComponentId = (componentType, index) => {
    const paddedIndex = String(index + 1).padStart(3, "0");
    return `${componentType}${paddedIndex}`;
};

// Generate unique ID for subcomponents (doors, windows, floor headers)
const generateSubcomponentId = (subcomponentType, index) => {
    const paddedIndex = String(index + 1).padStart(3, "0");
    return `${subcomponentType}${paddedIndex}`;
};

// Map Excel field names to component measurement properties
const mapMeasurements = (excelData, componentType) => {
    const measurements = {};

    // Common measurements
    if (excelData.Area !== undefined && excelData.Area !== null) {
        measurements.area = convertToMetric(excelData.Area, "area", "ft2");
    }
    if (excelData.Height !== undefined && excelData.Height !== null) {
        measurements.height = convertToMetric(excelData.Height, "length", "ft");
    }
    if (excelData.Width !== undefined && excelData.Width !== null) {
        measurements.width = convertToMetric(excelData.Width, "length", "ft");
    }
    if (excelData.Length !== undefined && excelData.Length !== null) {
        measurements.length = convertToMetric(excelData.Length, "length", "ft");
    }
    if (excelData.Perimeter !== undefined && excelData.Perimeter !== null) {
        measurements.perimeter = convertToMetric(excelData.Perimeter, "length", "ft");
    }

    // Component-specific measurements
    if (componentType === "wall") {
        if (excelData.Corners !== undefined && excelData.Corners !== null) {
            measurements.corners = excelData.Corners;
        }
        if (excelData.Intersections !== undefined && excelData.Intersections !== null) {
            measurements.intersections = excelData.Intersections;
        }
    }

    if (componentType === "basement" || componentType === "crawlspace") {
        if (excelData.Depth !== undefined && excelData.Depth !== null) {
            measurements.depth = convertToMetric(excelData.Depth, "length", "ft");
        }
    }

    if (componentType === "ceiling") {
        if (excelData.Heel !== undefined && excelData.Heel !== null) {
            measurements.heelHeight = convertToMetric(excelData.Heel, "length", "ft");
        }
        if (excelData.Slope !== undefined && excelData.Slope !== null) {
            // Slope might need special handling
            measurements.slope = {
                id: 0,
                value: parseFloat(excelData.Slope.toFixed(4)),
            };
        }
        if (excelData.EaveLength !== undefined && excelData.EaveLength !== null) {
            measurements.length = convertToMetric(excelData.EaveLength, "length", "ft");
        }
    }

    if (componentType === "floorHeader") {
        // Floor headers use height and perimeter
        if (measurements.height && measurements.perimeter) {
            measurements.area = measurements.height * measurements.perimeter;
        }
    }

    return measurements;
};

// Create a component from Excel group data
const createComponent = (groupName, groupData, componentType, componentIndex, category) => {
    // Check if component should be created

    if (groupData.Present !== true && groupData.Present !== "true" && groupData.Present !== 1) {
        return null;
    }

    // Get template
    const template = getComponentTemplate(componentType);
    if (!template) {
        console.warn(`No template found for component type: ${componentType}`);
        return null;
    }

    // Create component with template defaults
    const component = JSON.parse(JSON.stringify(template));

    // Set basic properties
    component.label = groupData.Label || groupName || component.label;
    component.label = String(component.label).trim();
    component.category = category || component.category;

    // Map measurements
    const measurements = mapMeasurements(groupData, componentType);

    // Handle special cases for different component types
    if (componentType === "basement" || componentType === "crawlspace" || componentType === "slab") {
        if (groupData.ExposedPerimeter !== undefined && groupData.ExposedPerimeter !== null) {
            component.exposedSurfacePerimeter = convertToMetric(groupData.ExposedPerimeter, "length", "ft");
        }

        if (groupData.Area !== undefined && groupData.Area !== null) {
            component.floor.measurements.area = convertToMetric(groupData.Area, "area", "ft2");
            component.floor.measurements.isRectangular = false;
        }

        if (groupData.Perimeter !== undefined && groupData.Perimeter !== null) {
            component.floor.measurements.perimeter = convertToMetric(groupData.Perimeter, "length", "ft");
        }

        if (component.exposedSurfacePerimeter > component.floor.measurements.perimeter) {
            component.exposedSurfacePerimeter = component.floor.measurements.perimeter;
        }

        if (
            ["basement", "crawlspace"].includes(componentType) &&
            groupData.WallHeight !== undefined &&
            groupData.WallHeight !== null
        ) {
            component.wall.measurements.height = convertToMetric(groupData.WallHeight, "length", "ft");

            component.wall.lintelInsType = {
                codeRef: "Lintel-S-2026062500000000",
                codeLabel: "101",
            };
        }

        if (
            ["basement", "crawlspace"].includes(componentType) &&
            groupData.WallDepthBelowGrade !== undefined &&
            groupData.WallDepthBelowGrade !== null
        ) {
            component.wall.measurements.depth = convertToMetric(groupData.WallDepthBelowGrade, "length", "ft");
        }

        if (
            ["basement"].includes(componentType) &&
            groupData.PonyWallCheck &&
            groupData.PonyWallHeight !== undefined &&
            groupData.PonyWallHeight !== null &&
            groupData.PonyWallHeight > 0
        ) {
            component.wall.ponyWall.enabled = true;
            component.wall.ponyWall.ponyWallHeight = convertToMetric(groupData.PonyWallHeight, "length", "ft");
            component.wall.ponyWall.ponyWallInsType = {
                codeRef: "UserSpecified",
                codeLabel: "User Specified",
            };
            component.wall.ponyWall.ponyWallInsType_composite = {
                section0: {
                    section: 1,
                    percentage: 100,
                    rsi: 0.1,
                    nominalRsi: 0.1,
                },
            };
            component.wall.ponyWall.ponyWallInsType_effRVal = 0.1;
            component.wall.ponyWall.ponyWallInsType_nomRVal = 0.1;
        }

        //Corners for: basement, crawlspace
        if (
            ["basement", "crawlspace"].includes(componentType) &&
            groupData.Corners !== undefined &&
            groupData.Corners !== null
        ) {
            component.wall.measurements.corners = parseInt(groupData.Corners);
        }
    } else {
        if (Object.keys(measurements).length > 0) {
            component.measurements = {
                ...component.measurements,
                ...measurements,
            };
        }

        if (componentType === "wall" || componentType === "expFloor") {
            if (groupData.BufferedCheck !== undefined && groupData.BufferedCheck !== null) {
                component.adjacentEnclosedSpace = groupData.BufferedCheck;
            }

            component.lintelInsType = {
                codeRef: "Lintel-S-2026062500000000",
                codeLabel: "101",
            };
        }

        if (componentType === "ceiling") {
            if (groupData.Type !== undefined && groupData.Type !== null && [0, 1, 2, 3, 4].includes(groupData.Type)) {
                //Type Code used for ceiling type
                console.log(groupName, groupData, componentType, componentIndex, category);
                console.log("Type", groupData);
                component.constructionType = { id: groupData.Type };

                if ([2, 3].includes(groupData.Type)) {
                    //Have to cover flat ceilings and their change of code type
                    component.ceilingInsType = {
                        codeLabel: "User Specified",
                        codeRef: "UserSpecified",
                    };
                    component.ceilingInsType_nomRVal = 0.1;
                    component.ceilingInsType_effRVal = 0.1;
                    component.ceilingInsType_warning = "";
                }
            }
        }
    }

    // Initialize subcomponents
    component.subcomponents = {};

    return component;
};

// Create a door from Excel door data
const createDoor = (doorData, doorIndex) => {
    const template = getComponentTemplate("door");
    if (!template) {
        return null;
    }

    const door = JSON.parse(JSON.stringify(template));

    door.label = doorData.Label || `Door ${doorIndex + 1}`;
    door.category = getCategoryFromLevel(doorData.Level);

    // Map measurements (doors use width and height in inches)
    if (doorData["Width (in)"] !== undefined && doorData["Width (in)"] !== null) {
        door.measurements.width = convertToMetric(doorData["Width (in)"], "length", "in");
    }
    if (doorData["Height (in)"] !== undefined && doorData["Height (in)"] !== null) {
        door.measurements.height = convertToMetric(doorData["Height (in)"], "length", "in");
    }
    if (door.measurements.width && door.measurements.height) {
        door.measurements.area = door.measurements.width * door.measurements.height;
    }

    if (doorData["Direction (Cardinal)"] !== undefined && doorData["Direction (Cardinal)"] !== null) {
        door.facingDirection = { id: compassDirectionMapToId[doorData["Direction (Cardinal)"].toUpperCase()] || 0 };
    }

    // Map door type if available
    if (doorData.Type !== undefined && doorData.Type !== null) {
        // This might need to be mapped to doorType.id based on your door type options
        // For now, we'll leave it as default

        const doorInputString = String(doorData.Type).toLowerCase().replace(/ /g, "");

        getOptions({ fieldKey: "doorType" }).forEach((opt) => {
            if (opt.label.toLowerCase().replace(/ /g, "") === doorInputString) {
                door.doorType = { id: opt.value.id, value: opt.value.value };
            }

            if (String(opt.value.id) == doorInputString) {
                door.doorType = { id: opt.value.id, value: opt.value.value };
            }
        });
    }

    door.subcomponents = {};

    return door;
};

// Create a window from Excel window data
const createWindow = (windowData, windowIndex) => {
    const template = getComponentTemplate("window");
    if (!template) {
        return null;
    }

    const window = JSON.parse(JSON.stringify(template));

    window.label = windowData.Label || `Window ${windowIndex + 1}`;
    window.category = getCategoryFromLevel(windowData.Level);

    // Map measurements (windows use width and height in inches, but stored in mm in template)
    if (windowData["Width (in)"] !== undefined && windowData["Width (in)"] !== null) {
        const widthM = convertToMetric(windowData["Width (in)"], "length", "in");
        window.measurements.width = widthM * 1000; // Convert to mm
    }
    if (windowData["Height (in)"] !== undefined && windowData["Height (in)"] !== null) {
        const heightM = convertToMetric(windowData["Height (in)"], "length", "in");
        window.measurements.height = heightM * 1000; // Convert to mm
    }
    if (window.measurements.width && window.measurements.height) {
        window.measurements.area = (window.measurements.width * window.measurements.height) / 1000000; // Convert mm2 to m2
    }

    // Map count
    if (windowData.Count !== undefined && windowData.Count !== null) {
        window.numIdentical = parseInt(windowData.Count) || 1;
    }

    // Map overhang dimensions (in feet)
    if (windowData["Overhang Width (ft)"] !== undefined && windowData["Overhang Width (ft)"] !== null) {
        window.measurements.overhangWidth = convertToMetric(windowData["Overhang Width (ft)"], "length", "ft");
    }
    if (windowData["Overhang Height (ft)"] !== undefined && windowData["Overhang Height (ft)"] !== null) {
        window.measurements.headerHeight = convertToMetric(windowData["Overhang Height (ft)"], "length", "ft");
    }

    // Map direction (cardinal)
    if (windowData["Direction (Cardinal)"] !== undefined && windowData["Direction (Cardinal)"] !== null) {
        window.facingDirection = { id: compassDirectionMapToId[windowData["Direction (Cardinal)"].toUpperCase()] || 0 };
    }

    // Window Type
    if (windowData.Type !== undefined && windowData.Type !== null) {
        //Only types supported are "223204", "223214", "223224", "223234", "223244" (patio door), "223254" (skylight)
        const windowType = String(windowData.Type).replace(/ /g, "");

        Object.entries(takeoffCodes).forEach(([key, value]) => {
            if (value.value == windowType) {
                window.windowType = { codeLabel: value.label, codeRef: key };
                window.windowType_nomRVal = value.nominalRValue;
                window.windowType_effRVal = value.nominalRValue;
                window.shgc = value.shgc;
                window.er = value.er;
            }
        });
    }

    return window;
};

// Create a floor header from Excel data
const createFloorHeader = (headerData, headerIndex) => {
    const template = getComponentTemplate("floorHeader");
    if (!template) {
        return null;
    }

    const header = JSON.parse(JSON.stringify(template));

    header.label = headerData.Label || `Floor Header ${headerIndex + 1}`;
    header.category = getCategoryFromLevel(headerData.Level);

    // Map measurements
    const measurements = mapMeasurements(headerData, "floorHeader");
    if (Object.keys(measurements).length > 0) {
        header.measurements = {
            ...header.measurements,
            ...measurements,
        };
    }

    const floorHeaderId = generateComponentId("floorHeader", headerIndex + 1);

    return { [floorHeaderId]: header };
};

/**
 * Converts parsed Excel data into modelData structure
 * @param {Object} parsedExcelData - The result from parseExcelFile
 * @returns {Object} modelData object ready to be used
 */
export const convertExcelToModelData = (parsedExcelData) => {
    // Start with default template
    const modelData = JSON.parse(JSON.stringify(defaultTemplate));

    // Initialize components if not present
    if (!modelData.components) {
        modelData.components = {};
    }
    if (!modelData.intComponents) {
        modelData.intComponents = {};
    }

    //Set units to Imperial
    modelData.uiSettings.primaryUnits = "Imperial";

    // Track component indices and subcomponent indices
    const componentIndices = {};
    let doorIndex = 0;
    let windowIndex = 0;
    let floorHeaderIndex = 0;
    let modelName = "No model name provided";

    // Map to store parent components by _ParentObject for subcomponent assignment
    const parentComponentMap = {};

    // Process modelSpecifications
    if (parsedExcelData.groups && parsedExcelData.groups.modelSpecifications) {
        const specs = parsedExcelData.groups.modelSpecifications;

        if (specs.FileName !== undefined && specs.FileName !== null) {
            modelName = specs.FileName;
        }

        if (specs.Evaluator !== undefined && specs.Evaluator !== null) {
            modelData.general.file.userNameEnteredBy = specs.Evaluator;
        }

        const evaluationDate = parseEvaluationDate(specs.Date);
        console.log("evaluationDate", evaluationDate);
        if (evaluationDate) {
            modelData.general.file.evaluationDate = evaluationDate;
        }

        // Map _NumberOfBedrooms
        if (specs.NumberOfBedrooms !== undefined && specs.NumberOfBedrooms !== null) {
            modelData.ventilation.rooms.bedrooms = parseInt(specs.NumberOfBedrooms) || 0;
        }

        // Map _Volume
        if (specs.Volume !== undefined && specs.Volume !== null) {
            modelData.dimensions.volume.total = convertToMetric(specs.Volume, "volume", "ft3");

            modelData.dimensions.volume.items.item0003 = {
                value: modelData.dimensions.volume.total,
                enabled: true,
                description: "Total",
            };
        }

        //AboveGradeHeatedFloorArea
        if (specs.AboveGradeHeatedFloorArea !== undefined && specs.AboveGradeHeatedFloorArea !== null) {
            modelData.dimensions.aboveGradeIntFloorArea.total = convertToMetric(
                specs.AboveGradeHeatedFloorArea,
                "area",
                "ft2"
            );

            modelData.dimensions.aboveGradeIntFloorArea.items.item0001 = {
                value: modelData.dimensions.aboveGradeIntFloorArea.total,
                enabled: true,
                description: "Total",
            };
        }

        //BelowGradeHeatedFloorArea
        if (specs.BelowGradeHeatedFloorArea !== undefined && specs.BelowGradeHeatedFloorArea !== null) {
            modelData.dimensions.belowGradeIntFloorArea.total = convertToMetric(
                specs.BelowGradeHeatedFloorArea,
                "area",
                "ft2"
            );

            modelData.dimensions.belowGradeIntFloorArea.items.item0002 = {
                value: modelData.dimensions.belowGradeIntFloorArea.total,
                enabled: true,
                description: "Total",
            };
        }

        // Map _HeightOfHighestCeiling
        if (specs.HeightOfHighestCeiling !== undefined && specs.HeightOfHighestCeiling !== null) {
            modelData.dimensions.highestCeiling.total = convertToMetric(specs.HeightOfHighestCeiling, "length", "ft");
        }

        //AttachedArea
        if (specs.AttachedArea !== undefined && specs.AttachedArea !== null) {
            modelData.airTightness.commonSurfaces.walls = convertToMetric(specs.AttachedArea, "area", "ft2");
            modelData.airTightness.commonSurfaces.total = modelData.airTightness.commonSurfaces.walls;
        }

        //ExposedAndBufferedArea
        if (specs.ExposedAndBufferedArea !== undefined && specs.ExposedAndBufferedArea !== null) {
            modelData.airTightness.buildingSurfaceArea = convertToMetric(specs.ExposedAndBufferedArea, "area", "ft2");
        }

        //BuildingSurfaceArea Override
        if (specs.TotalArea !== undefined && specs.TotalArea !== null) {
            modelData.airTightness.buildingSurfaceAreaOverride.enabled = true;
            modelData.airTightness.buildingSurfaceAreaOverride.totalArea = convertToMetric(
                specs.TotalArea,
                "area",
                "ft2"
            );
            modelData.airTightness.buildingSurfaceArea = modelData.airTightness.buildingSurfaceAreaOverride.totalArea;
        }

        //YearBuilt
        if (specs.YearBuilt !== undefined && specs.YearBuilt !== null) {
            modelData.specifications.yearBuilt = { id: 0, value: parseInt(specs.YearBuilt) || 0 };
        }

        //Number of Storeys
        if (specs.NumberOfStoreys !== undefined && specs.NumberOfStoreys !== null) {
            const numStoreysMapToId = {
                1: 0,
                2: 2,
                3: 4,
            };

            if (typeof specs.NumberOfStoreys === "string") {
                const storeyOpts = getOptions({ fieldKey: "numStoreys" });
                const storeyMatch = storeyOpts.find(
                    (opt) =>
                        opt.label.toLowerCase().replace(/ /g, "") ===
                        specs.NumberOfStoreys.toLowerCase().replace(/ /g, "")
                );
                if (storeyMatch) {
                    modelData.specifications.numStoreys = { id: storeyMatch.value.id };
                } else {
                    console.warn(`Unknown number of storeys: ${specs.NumberOfStoreys}`);
                }
            } else if (typeof specs.NumberOfStoreys === "number") {
                modelData.specifications.numStoreys = { id: numStoreysMapToId[specs.NumberOfStoreys] || 0 };
            } else {
                console.warn(`Unknown number of storeys: ${specs.NumberOfStoreys}`);
            }
        }

        //Building Type
        if (specs.BuildingType !== undefined && specs.BuildingType !== null) {
            const buildingSubTypeHouseOpts = getOptions({ fieldKey: "buildingSubType", indKey: 0 });
            const buildingSubTypeMurbOpts = getOptions({ fieldKey: "buildingSubType", indKey: 2 });

            const houseTypeMatch = buildingSubTypeHouseOpts.find(
                (opt) =>
                    opt.label.toLowerCase().replace(/ /g, "") === specs.BuildingType.toLowerCase().replace(/ /g, "")
            );
            const murbTypeMatch = buildingSubTypeMurbOpts.find(
                (opt) =>
                    opt.label.toLowerCase().replace(/ /g, "") === specs.BuildingType.toLowerCase().replace(/ /g, "")
            );

            if (houseTypeMatch) {
                modelData.specifications.buildingType = { id: 0 };
                modelData.specifications.buildingSubType = { id: houseTypeMatch.value.id };
            } else if (murbTypeMatch) {
                modelData.specifications.buildingType = { id: 2 };
                modelData.specifications.buildingSubType = { id: murbTypeMatch.value.id };
            } else {
                console.warn(`Unknown building type: ${specs.BuildingType}`);
            }
        }

        //FrontOrientation
        if (specs.FrontOrientation !== undefined && specs.FrontOrientation !== null) {
            modelData.specifications.facingDirection = {
                id: compassDirectionMapToId[specs.FrontOrientation.toUpperCase()] || 0,
            };
        }

        //Plan Shape
        if (specs.PlanShapeCode !== undefined && specs.PlanShapeCode !== null) {
            const planShapeOpts = getOptions({ fieldKey: "planShape" });
            const code = specs.PlanShapeCode;
            // Try numeric ID first
            if (typeof code === "number" || !isNaN(parseInt(code))) {
                //This one is used
                modelData.specifications.planShape = { id: parseInt(code) };
            } else {
                //Back-up case
                // Try label match (same pattern as BuildingType / NumberOfStoreys)
                const match = planShapeOpts.find(
                    (opt) => opt.label.toLowerCase().replace(/ /g, "") === String(code).toLowerCase().replace(/ /g, "")
                );
                if (match) {
                    modelData.specifications.planShape = { id: match.value.id };
                } else {
                    console.warn(`Unknown plan shape: ${code}`);
                }
            }
        }

        //Basement thermostat
    }

    // Process groups to create components

    const { groups, doors, windows } = parsedExcelData;

    const floorHeaders = [];
    let hasBasement = false;
    Object.entries(parsedExcelData.groups || {}).forEach(([groupName, groupData]) => {
        // Skip modelSpecifications as it's already processed
        if (groupName === "modelSpecifications") {
            return;
        }

        if (groupName.includes("Headers")) {
            if (groupData.Present == true) {
                floorHeaders.push({ id: groupName, ...groupData });
            }

            return;
        }

        // Get component type from group name
        const componentType = GROUP_TO_COMPONENT_TYPE[groupName];
        if (!componentType) {
            console.warn(`Unknown group name: ${groupName}`);
            return;
        }

        if (componentType === "basement") {
            hasBasement = true;
        }

        // Get category from level
        const category = getCategoryFromLevel(groupData.Level);

        // Initialize component index for this type if needed
        if (!componentIndices[componentType]) {
            componentIndices[componentType] = 0;
        }

        // Create component
        const component = createComponent(
            groupName,
            groupData,
            componentType,
            componentIndices[componentType],
            category
        );

        if (!component) {
            return; // Skip if component shouldn't be created
        }

        // Generate component ID
        const componentId = generateComponentId(componentType, componentIndices[componentType]);
        componentIndices[componentType]++;

        // Initialize component type in components if needed
        if (!modelData.components[componentType]) {
            modelData.components[componentType] = {};
        }

        // Store component
        modelData.components[componentType][componentId] = component;

        // Store in parent map using ParentObject

        parentComponentMap[groupName] = {
            type: componentType,
            id: componentId,
        };
    });

    // Process doors
    (parsedExcelData.doors || []).forEach((doorData) => {
        const door = createDoor(doorData, doorIndex);

        if (!door) {
            return;
        }

        const doorId = generateSubcomponentId("door", doorIndex);
        doorIndex++;

        // Find parent component using _ParentObject
        const parentObject = doorData.ParentObject;
        if (!parentObject) {
            console.warn(`Door ${doorData.id} has no ParentObject`);
            return;
        }

        let doorPlaced = false;

        // First try direct lookup in parentComponentMap
        if (parentComponentMap[parentObject]) {
            const parent = parentComponentMap[parentObject];
            const parentComponent = modelData.components[parent.type]?.[parent.id];

            if (parentComponent) {
                if (!parentComponent.subcomponents) {
                    parentComponent.subcomponents = {};
                }
                if (!parentComponent.subcomponents.door) {
                    parentComponent.subcomponents.door = {};
                }
                parentComponent.subcomponents.door[doorId] = door;
                doorPlaced = true;
            }
        }

        // If not found, try to match by label
        if (!doorPlaced) {
            Object.keys(modelData.components).forEach((compType) => {
                if (doorPlaced) return;
                Object.keys(modelData.components[compType] || {}).forEach((compId) => {
                    if (doorPlaced) return;
                    const comp = modelData.components[compType][compId];
                    if (comp.label === parentObject || compId === parentObject || doorData.ParentLabel === comp.label) {
                        if (!comp.subcomponents) {
                            comp.subcomponents = {};
                        }
                        if (!comp.subcomponents.door) {
                            comp.subcomponents.door = {};
                        }
                        comp.subcomponents.door[doorId] = door;
                        doorPlaced = true;
                    }
                });
            });
        }

        if (!doorPlaced) {
            console.warn(`Could not find parent component for door: ${doorData.id}, ParentObject: ${parentObject}`);
        }
    });

    // Process windows
    (parsedExcelData.windows || []).forEach((windowData) => {
        const window = createWindow(windowData, windowIndex);
        if (!window) {
            return;
        }

        const windowId = generateSubcomponentId("window", windowIndex);
        windowIndex++;

        // Find parent component using _ParentObject
        const parentObject = windowData.ParentObject;
        if (!parentObject) {
            console.warn(`Window ${windowData.id} has no ParentObject`);
            return;
        }

        // First, check if parent is a door by searching all components for doors
        let windowPlaced = false;
        Object.keys(modelData.components).forEach((compType) => {
            if (windowPlaced) return;
            Object.keys(modelData.components[compType] || {}).forEach((compId) => {
                if (windowPlaced) return;
                const comp = modelData.components[compType][compId];
                if (comp.subcomponents?.door) {
                    Object.keys(comp.subcomponents.door).forEach((doorId) => {
                        if (windowPlaced) return;
                        // Check if this door matches the parent object (by ID or label)
                        if (
                            doorId === parentObject ||
                            comp.subcomponents.door[doorId].label === parentObject ||
                            windowData.ParentLabel === comp.subcomponents.door[doorId].label
                        ) {
                            if (!comp.subcomponents.door[doorId].subcomponents) {
                                comp.subcomponents.door[doorId].subcomponents = {};
                            }
                            if (!comp.subcomponents.door[doorId].subcomponents.window) {
                                comp.subcomponents.door[doorId].subcomponents.window = {};
                            }
                            comp.subcomponents.door[doorId].subcomponents.window[windowId] = window;
                            windowPlaced = true;
                        }
                    });
                }
            });
        });

        // If not placed on a door, try to place on a regular component
        if (!windowPlaced && parentComponentMap[parentObject]) {
            const parent = parentComponentMap[parentObject];
            const parentComponent = modelData.components[parent.type]?.[parent.id];

            if (parentComponent) {
                // Window is on a regular component (wall, ceiling, basement, crawlspace)
                if (!parentComponent.subcomponents) {
                    parentComponent.subcomponents = {};
                }
                if (!parentComponent.subcomponents.window) {
                    parentComponent.subcomponents.window = {};
                }
                parentComponent.subcomponents.window[windowId] = window;
                windowPlaced = true;
            }
        }

        if (!windowPlaced) {
            console.warn(`Could not find parent component for window: ${windowData.id}, ParentObject: ${parentObject}`);
        }
    });

    //Apply all takeoff codes to the model
    modelData.codes = {
        ...modelData.codes,
        ...takeoffCodes,
    };

    // Process floor headers
    (floorHeaders || []).forEach((floorHeaderData) => {
        const floorHeader = createFloorHeader(floorHeaderData, floorHeaderIndex); //Built with { [floorHeaderId]: header }

        if (!floorHeader || floorHeaderData.ParentObject.includes("SOG")) {
            return;
        }

        if (floorHeaderData.ParentObject && parentComponentMap[floorHeaderData.ParentObject]) {
            const { type: parentType, id: parentId } = parentComponentMap[floorHeaderData.ParentObject];
            const parentComponent = modelData.components[parentType]?.[parentId];
            if (parentComponent) {
                parentComponent.subcomponents.floorHeader = {
                    ...parentComponent.subcomponents.floorHeader,
                    ...floorHeader, //Built with { [floorHeaderId]: header }
                };
            }
        }
        floorHeaderIndex++;
    });

    if (hasBasement) {
        modelData.domesticHotWater.primarySystem.tankLocation = { id: 1 };
        modelData.temperatures.basement.separateThermostat = false;
    }

    return { modelName, modelData: removeUndefined(modelData) };
};
